import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A value that is edited on screen and written whole, one write at a time.
 *
 * The edit shows at once. Writes go out in order and never overlap: an edit made while one is in
 * flight waits, and only the newest waiting edit is sent — the value is written whole, so the ones
 * in between would be overwritten anyway. Overlapping writes could come back in the wrong order and
 * put an older state back on screen after a newer one.
 *
 * What is stored replaces what is on screen only while nothing is waiting, so a server answer to an
 * earlier write does not undo a later edit. A failed write shows its error and falls back to what is
 * stored.
 */
export function useQueuedWrite<T>(
    stored: T,
    write: (next: T) => Promise<void>,
    describeError: (error: any) => string,
): { value: T; apply: (next: T) => void; error: string | null } {
    const [value, setValue] = useState<T>(stored);
    const [error, setError] = useState<string | null>(null);
    const inFlight = useRef(false);
    const queued = useRef<{ next: T } | null>(null);
    const latestStored = useRef(stored);
    latestStored.current = stored;

    useEffect(() => {
        if (!inFlight.current && !queued.current) setValue(stored);
    }, [stored]);

    const send = useCallback(
        async (next: T) => {
            inFlight.current = true;
            try {
                await write(next);
                setError(null);
            } catch (writeError: any) {
                console.error('Could not save', writeError);
                // A newer edit is already waiting and writes the whole value again; its outcome is
                // the one to report. Showing this failure would claim a state that is about to be
                // replaced — and nothing would clear the message if that write succeeds.
                if (!queued.current) {
                    setError(describeError(writeError));
                    setValue(latestStored.current);
                }
            } finally {
                inFlight.current = false;
                const waiting = queued.current;
                queued.current = null;
                if (waiting) void send(waiting.next);
            }
        },
        [write, describeError],
    );

    const apply = useCallback(
        (next: T) => {
            setValue(next);
            setError(null);
            if (inFlight.current) queued.current = { next };
            else void send(next);
        },
        [send],
    );

    return { value, apply, error };
}
